(function () {
	// Production version of the theme attribution capture script.
	// Deploy marker: 2026-05-29 clean prod, no browser logging.
	//
	// What this script does:
	// 1. Reads query-string params from the landing URL.
	// 2. Lowercases/normalizes param keys, but preserves values exactly as sent.
	// 3. Drops Shopify/site-control params from PAGE_SPECIFIC_PARAMS.
	// 4. Stores the remaining attribution params in the np_landing_params cookie.
	// 5. Posts them to /cart/update.js so Shopify carries them into order
	//    Additional details / customAttributes at checkout.
	//
	// This file intentionally captures "all non-page-specific params" instead of
	// a strict whitelist, so new marketing partner params are not silently lost.
	// It intentionally has no browser logging so production stays clean.
	//
	// Keep behavior aligned with:
	//   utilities/landing-params-contract.ts (test contract)
	//   nativepath-v2/app/lib/cookies.ts (Hydrogen capture)

	var KEY_SYNONYMS = {
		// Checkout Champ and older links may send hit IDs under several names.
		// Shopify order attributes should use one canonical key: hitid.
		hit: 'hitid',
		hit_id: 'hitid',
		cc_custom_hitid: 'hitid',
		// Analytics libraries may send the anonymous user ID under old names.
		// Shopify order attributes should use one canonical key: anonymousid.
		rudderanonymousid: 'anonymousid',
		ajs_id: 'anonymousid',
	};

	// These query params control storefront behavior, filters, redirects,
	// previews, checkout, or product choices. They are not attribution.
	// Anything in this list is intentionally blocked from cart/order attributes.
	var PAGE_SPECIFIC_PARAMS = [
		'q',
		'blogs',
		'isblog',
		'filter',
		'minprice',
		'maxprice',
		'productmetafield',
		'sort',
		'cursor',
		'direction',
		'subscription',
		'title',
		'selling_plan',
		'subscription_discount',
		'discount',
		'to',
		'payment',
		'checkout_url',
		'size',
		'sizes',
		'flavor',
		'flavors',
		'passwordupdated',
		'logged_in',
		'_auth',
		'redirect',
		'return_to',
		'key',
		'format',
	];

	// Shopify /cart/update.js merges attributes by key. If we omit a key that was
	// already on the cart, Shopify leaves the old value in place.
	//
	// On a fresh attributed landing, this list makes us send blank values for the
	// known attribution keys that are missing from the current URL. That prevents
	// old UTMs/click IDs from leaking into the next order.
	//
	// Unknown partner params are still supported:
	// - New unknown params in the current URL are written.
	// - Unknown params from the previous cookie are also blanked when omitted
	//   from the current landing URL.
	//
	// If an unknown value exists only on Shopify's cart and not in this cookie,
	// the script cannot know its key, so it cannot clear that one automatically.
	var KNOWN_ATTRIBUTION_KEYS_TO_CLEAR = [
		'cohort',
		'utm_source',
		'utm_medium',
		'utm_campaign',
		'utm_content',
		'utm_term',
		'gclid',
		'fbclid',
		'cjevent',
		'hitid',
		'_kx',
		'anonymousid',
	];

	var COOKIE = 'np_landing_params';

	function hasOwn(obj, key) {
		// Use hasOwnProperty safely so falsey values are still treated as present.
		return Object.prototype.hasOwnProperty.call(obj, key);
	}

	function isPageSpecificParam(key) {
		// Keep page-control params out of order attribution.
		return PAGE_SPECIFIC_PARAMS.indexOf(key) >= 0;
	}

	function normalizeKey(raw) {
		// Normalize keys only. Values are intentionally not lowercased or sanitized.
		if (!raw) return '';
		var trimmed = String(raw).trim();
		if (!trimmed) return '';
		var lower = trimmed.toLowerCase();
		return KEY_SYNONYMS[lower] || lower;
	}

	// Convert raw query/cookie pairs into the exact object we are willing to send
	// to Shopify. Empty values are dropped so "?utm_source=" does not wipe the
	// cookie by accident; stale clearing is handled separately below.
	function normalizeEntries(entries) {
		var collected = {};
		for (var i = 0; i < entries.length; i++) {
			var key = normalizeKey(entries[i][0]);
			var value = entries[i][1];
			if (!key || !value || isPageSpecificParam(key)) continue;
			collected[key] = value;
		}
		return Object.keys(collected).length > 0 ? collected : null;
	}

	function readCookie(name) {
		// Regex: `(?:^|; )` matches start of cookie string or immediately after `; `;
		//        `name + '=([^;]*)'` captures up to the next semicolon.
		// Example: from `foo=1; np_landing_params=%7B%7D; bar=2` captures `%7B%7D`.
		var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
		return m ? decodeURIComponent(m[1]) : '';
	}

	function writeCookie(name, value) {
		// SameSite=Lax keeps the cookie available across normal site navigation
		// without allowing third-party contexts to read/write it.
		var parts = [name + '=' + encodeURIComponent(value), 'Path=/', 'SameSite=Lax'];
		// Regex: `(^|\.)nativepath\.com$` matches the bare domain or any subdomain.
		// Example matches: `nativepath.com`, `shop.nativepath.com`.
		// Example non-match: `nativepath-dev2.myshopify.com`.
		if (/(^|\.)nativepath\.com$/.test(location.hostname)) {
			// Production should share attribution across nativepath.com subdomains.
			// Dev2 is a myshopify.com host, so it keeps the default host cookie.
			parts.splice(1, 0, 'Domain=.nativepath.com');
		}
		if (location.protocol === 'https:') parts.push('Secure');
		document.cookie = parts.join('; ') + ';';
	}

	function collectCurrentUrlParams() {
		// URLSearchParams handles decoding and repeated params consistently.
		// If the URL cannot be parsed, fail closed and rely on the existing cookie.
		try {
			var entries = [];
			new URL(location.href).searchParams.forEach(function (value, rawKey) {
				entries.push([rawKey, value]);
			});
			return normalizeEntries(entries);
		} catch (e) {
			return null;
		}
	}

	function readLandingParamsCookie() {
		// The cookie lets attribution survive PDP -> cart -> checkout navigation
		// after the original query string is no longer present.
		var raw = readCookie(COOKIE);
		if (!raw) return null;
		try {
			var parsed = JSON.parse(raw) || {};
			var entries = Object.keys(parsed).map(function (k) {
				return [k, parsed[k]];
			});
			return normalizeEntries(entries);
		} catch (e) {
			return null;
		}
	}

	var previousParams = readLandingParamsCookie();
	var currentParams = collectCurrentUrlParams();

	if (currentParams) {
		// A new attributed landing replaces the cookie with the latest values.
		// That gives us last-touch behavior for future pages in this session.
		writeCookie(COOKIE, JSON.stringify(currentParams));
	}

	// Prefer the current URL when it has attribution. Otherwise reuse the cookie
	// so checkout still receives the original landing attribution on later pages.
	var params = currentParams || previousParams;
	if (!params) return;

	// Set of keys to include in the /cart/update.js request.
	// - Active keys get their current values.
	// - Missing known/prior keys get blanks, because Shopify only clears a cart
	//   attribute when we explicitly send an empty value for that key.
	var keysToWrite = {};
	if (currentParams) {
		KNOWN_ATTRIBUTION_KEYS_TO_CLEAR.forEach(function (key) {
			keysToWrite[key] = true;
		});
		Object.keys(previousParams || {}).forEach(function (key) {
			keysToWrite[key] = true;
		});
	}
	Object.keys(params).forEach(function (key) {
		keysToWrite[key] = true;
	});

	var body = [];
	Object.keys(keysToWrite).forEach(function (key) {
		// If the param is present, write its exact value. If it is stale, send an
		// empty value so Shopify clears the cart attribute.
		var value = hasOwn(params, key) ? params[key] : '';
		body.push('attributes[' + encodeURIComponent(key) + ']=' + encodeURIComponent(value));
	});

	fetch('/cart/update.js', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
			Accept: 'application/json',
		},
		body: body.join('&'),
		// Shopify identifies the current cart through same-origin cart cookies.
		// Including credentials ties this update to the shopper's active cart.
		credentials: 'same-origin',
	}).catch(function () {
		// Production stays silent. A cart update failure should not block the page.
	});
})();
